import { Button, Layout, Row, Space } from 'antd';
import { config } from 'config/config';
import routePaths from 'config/routes';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import * as api from 'services/api';
import BasicMenu from 'src/components/layout/BasicMenu';
import Link from 'src/components/Link';
import DrawerMenu from 'src/components/menu/DrawerMenu';
import { getMenu } from 'src/components/menu/Menu.utils';
import SignInHeader from 'src/components/signInHeader/SignInHeader';
import SignInHeaderConsumer from 'src/components/signInHeader/SignInHeaderConsumer';
import { IRootReducers } from 'src/reducers';
import { UserStatusEnum } from 'src/shared/enums';
import { IMenu } from 'src/shared/models';
import { isConPlatform } from 'utils';

type Props = ReturnType<typeof mapStateToProps>;

const BasicHeader: FC<Props> = ({
  authenticated,
  impersonate,
  publicMenu,
  privateMenu,
  user,
  userStatus,
}): JSX.Element => {
  const [collapsed, setCollapse] = useState(false);
  const [menu, setMenu] = useState<IMenu[]>(
    getMenu(
      authenticated,
      publicMenu,
      privateMenu,
      userStatus,
      user?.roles?.primaryRole || null,
      user?.dermo?.isDermo,
    ),
  );
  const [cartItems, setCartItems] = useState(0);
  const { formatMessage } = useIntl();

  useEffect(() => {
    setMenu(
      getMenu(
        authenticated,
        publicMenu,
        privateMenu,
        userStatus,
        user?.roles?.primaryRole || null,
        user?.dermo?.isDermo,
      ),
    );
  }, [authenticated, publicMenu, privateMenu, user?.dermo]);

  const handleCollapse = (customCollapsed: boolean | undefined): void => {
    if (customCollapsed) {
      setCollapse(customCollapsed);
    } else {
      setCollapse(!collapsed);
    }
  };

  const componentLogo = (
    <Link href="/">
      <img
        className="logo"
        src={`${config.APP.ASSET_PREFIX}/isdin_logo.png`}
        onClick={() => handleCollapse(false)}
      />
    </Link>
  );

  const componentButtons = !authenticated && !isConPlatform && (
    <Space size="large">
      <Link href={routePaths.PAGES.LOGIN} as="/login">
        <Button className=" btn-primary btn-primary">
          <FormattedMessage id="app.sign-in" />
        </Button>
      </Link>
      <Link href={routePaths.PAGES.SIGNUP} as="/register">
        <Button type="primary" className="btn-primary btn-primary--black">
          <FormattedMessage id="app.register" />
        </Button>
      </Link>
    </Space>
  );

  const componentCustomMenu = authenticated && (
    <BasicMenu
      className={`menu ${isConPlatform && 'menu-con'}`}
      menu={menu}
      mode="horizontal"
      userStatus={userStatus}
    />
  );

  const componentSignInHeader = authenticated && !isConPlatform && (
    <SignInHeader cartItems={cartItems} />
  );

  const componentImpersonate = impersonate && (
    <Row justify="center" align="middle" className="layout__impersonate">
      <span>
        You are logged as <strong>{user.email}</strong>
      </span>
    </Row>
  );

  const componentUserStatus = () => {
    let message: string;

    switch (userStatus) {
      case UserStatusEnum.ACCOUNT_NOT_VALIDATED:
      case UserStatusEnum.MANAGER_NOT_VALIDATED:
        message = formatMessage({ id: 'home.header.no-validated' });
        break;
      case UserStatusEnum.NO_CENTER:
        message = formatMessage({ id: 'home.header.no-center' });
        break;
      case UserStatusEnum.HCP_NOT_VALIDATED:
      case UserStatusEnum.STAFF_NOT_VALIDATED:
        message = formatMessage({ id: 'home.header.employee-pending' });
        break;
      default:
        break;
    }

    return (
      message && (
        <Row
          justify="center"
          align="middle"
          className="layout__account-validation">
          <span>{message}</span>
        </Row>
      )
    );
  };

  const headerClassName =
    'container--no-padding basicHeader__container basicHeader__container-fixed';

  const justifyIcon =
    isConPlatform && !authenticated ? "center" : "space-between";

  return (
    <div className={`header-shadow header-fixed ${headerClassName}`}>
      {componentImpersonate}
      {componentUserStatus()}
      {componentSignInHeader}
      <div className="container">
        <Layout.Header className="basicHeader">
          <Row
            justify={justifyIcon}
            align="middle"
            className={`basicHeader__wrapper--desktop ${
              isConPlatform && 'basicHeader__wrapper--desktop--no-wrap'
            }`}>
            {componentLogo}
            {componentButtons}
            {componentCustomMenu}
            <SignInHeaderConsumer />
          </Row>
          <Row align="middle" className="basicHeader__wrapper--mobile">
            <DrawerMenu loggedMenu={menu} cartItems={cartItems} />
          </Row>
        </Layout.Header>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
    authenticated:
      state.auth.accessToken !== null && state.auth.accessToken !== '',
    impersonate: state.auth.impersonate,
    publicMenu: state.app.publicMenu,
    privateMenu: state.app.userMenu,
    userStatus: state.auth.userStatus,
  };
};

export default connect(mapStateToProps, null)(BasicHeader);
